/**
 * Importações (F3b) — porte de src/pages/Importacoes do app atual, mesmas regras
 * (packages/core/services/importacaoService): receber dá entrada no estoque (com
 * séries), estornar reverte, editar reconcilia por modelo, custo landed rateado,
 * e a ALOCAÇÃO a cliente é a única baixa de estoque (pode deixar o saldo negativo:
 * pré-venda). Excluir com alocações é bloqueado pelo núcleo.
 */
import { createEffect, createMemo, createResource, createSignal, For, Index, on, Show } from 'solid-js'
import { Button } from '@ui/components/Button'
import { Dialog } from '@ui/components/Dialog'
import { Input } from '@ui/components/Input'
import type { Cliente, ImportacaoDetalhe, ModeloLista, StatusImportacao } from '@shared/types'
import { importacaoSchema } from '@shared/validation'
import { api, versaoDados } from '../nucleo/cliente'
import { mensagemErro } from '../lib/erro'
import { AreaTexto, brl, Campo, confirmar, criarRecarga, Erro, Etiqueta, Formulario, num, Painel, Selecao, T, type Tom, Vazio } from '../ui/kit'
import { BuscaModelo } from './Estoque'

const STATUS: StatusImportacao[] = ['PEDIDO', 'EM_TRANSITO', 'DESEMBARACO', 'RECEBIDO', 'CANCELADO']
const TOM: Record<StatusImportacao, Tom> = { PEDIDO: 'neutro', EM_TRANSITO: 'destaque', DESEMBARACO: 'aviso', RECEBIDO: 'sucesso', CANCELADO: 'perigo' }
const MANUAIS: StatusImportacao[] = ['PEDIDO', 'EM_TRANSITO', 'DESEMBARACO', 'CANCELADO']
const EDITAVEL: StatusImportacao[] = ['PEDIDO', 'EM_TRANSITO', 'DESEMBARACO']

export function Importacoes(props: { podeEditar: boolean }) {
  const [status, setStatus] = createSignal<StatusImportacao | ''>('')
  const [buscaPedido, setBuscaPedido] = createSignal('')
  const [buscaProduto, setBuscaProduto] = createSignal('')
  const [detalheId, setDetalheId] = createSignal<number | null>(null)
  const [form, setForm] = createSignal<{ det: ImportacaoDetalhe | null } | null>(null)
  const [recarga, recarregar] = criarRecarga()
  const [lista] = createResource(
    () => ({ s: status(), p: buscaPedido(), q: buscaProduto(), v: versaoDados(), r: recarga() }),
    (k) => api.importacoes.list(k.s || undefined, k.p.trim() || undefined, k.q.trim() || undefined),
  )

  const listaEl = (
    <div class="flex flex-col gap-4 p-4 sm:p-6">
      <div class="flex flex-wrap items-center gap-3">
        <div class="mr-auto">
          <h1 class="text-xl font-semibold text-ink">Importações</h1>
          <p class="text-xs text-ink-subtle">Receber dá entrada automática no estoque. Alocar a um cliente dá a baixa.</p>
        </div>
        <Show when={props.podeEditar}>
          <Button variant="accent" size="sm" onClick={() => setForm({ det: null })}>Nova importação</Button>
        </Show>
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <Selecao class="w-48" value={status()} onChange={(e) => setStatus(e.currentTarget.value as StatusImportacao | '')} aria-label="Status">
          <option value="">Todos os status</option>
          <For each={STATUS}>{(s) => <option value={s}>{s}</option>}</For>
        </Selecao>
        <Input class="w-72 max-w-full" placeholder="Filtrar por modelo, produto, linha ou cliente…" value={buscaProduto()} onInput={(e) => setBuscaProduto(e.currentTarget.value)} />
        <Input class="w-64 max-w-full" placeholder="Filtrar por pedido ou cliente…" value={buscaPedido()} onInput={(e) => setBuscaPedido(e.currentTarget.value)} />
      </div>
      <Show when={(lista.latest ?? []).length > 0} fallback={<Vazio titulo={lista.loading ? 'Carregando…' : 'Nenhuma importação'} texto="Cadastre um pedido à RSBP para começar." />}>
        <div class={T.caixa}>
          <table class={T.tabela}>
            <thead class={T.cab}>
              <tr>
                <th class={T.th}>Invoice</th>
                <th class={`${T.th} hidden md:table-cell`}>Fornecedor</th>
                <th class={T.th}>Status</th>
                <th class={`${T.th} text-right`}>Itens</th>
                <th class={`${T.th} text-right`}>FOB</th>
                <th class={`${T.th} hidden md:table-cell`}>Pedido</th>
              </tr>
            </thead>
            <tbody>
              <For each={lista.latest}>
                {(imp) => (
                  <tr class={`${T.tr} cursor-pointer`} classList={{ 'bg-hover': detalheId() === imp.id }} onClick={() => setDetalheId(imp.id)}>
                    <td class={`${T.td} font-medium text-ink`}>{imp.numeroInvoice}</td>
                    <td class={`${T.td} text-ink-muted hidden md:table-cell`}>{imp.fornecedorNome}</td>
                    <td class={T.td}><Etiqueta tom={TOM[imp.status]}>{imp.status}</Etiqueta></td>
                    <td class={`${T.td} text-right tabular-nums text-ink`}>{imp.qtdItens}</td>
                    <td class={`${T.td} text-right tabular-nums text-ink whitespace-nowrap`}>{num(imp.fobOrigem)} {imp.moeda}</td>
                    <td class={`${T.td} text-ink-subtle hidden md:table-cell`}>{imp.dataPedido || '—'}</td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
      </Show>
    </div>
  )

  // detalhe num pop-up sobre a lista (como no app antigo): a tabela de itens
  // ganha a largura toda e mostra todos os produtos, rolando o pop-up inteiro
  return (
    <>
      <div class="h-full overflow-auto">{listaEl}</div>
      <Dialog open={detalheId() !== null} onOpenChange={(o) => !o && setDetalheId(null)} position="center" class="w-auto max-w-[calc(100vw-16px)]">
        <div style={{ width: 'min(960px, calc(100vw - 32px))' }}>
          <Show when={detalheId()}>
            {(id) => (
              <DetalheImportacao
                id={id()}
                podeEditar={props.podeEditar}
                recarga={recarga()}
                aoFechar={() => setDetalheId(null)}
                aoMudar={recarregar}
                aoExcluida={() => (setDetalheId(null), recarregar())}
                aoEditar={(det) => setForm({ det })}
              />
            )}
          </Show>
        </div>
      </Dialog>
      <Show when={form()}>
        <FormImportacao importacao={form()!.det} aoFechar={() => setForm(null)} aoSalvar={() => (setForm(null), recarregar())} />
      </Show>
    </>
  )
}

function DetalheImportacao(props: {
  id: number
  podeEditar: boolean
  recarga: number
  aoFechar: () => void
  aoMudar: () => void
  aoExcluida: () => void
  aoEditar: (det: ImportacaoDetalhe) => void
}) {
  const [recargaLocal, recarregar] = criarRecarga()
  const [d] = createResource(() => ({ id: props.id, v: versaoDados(), r: props.recarga, l: recargaLocal() }), ({ id }) => api.importacoes.get(id))
  const [clientes] = createResource(() => api.clientes.list())
  const [modo, setModo] = createSignal<'ver' | 'receber'>('ver')
  const [series, setSeries] = createSignal<Record<number, string[]>>({})
  const [erro, setErro] = createSignal<string | null>(null)
  const [processando, setProcessando] = createSignal(false)
  const [alocItem, setAlocItem] = createSignal<number | null>(null)
  createEffect(on(() => props.id, () => (setModo('ver'), setAlocItem(null), setErro(null))))

  const executar = async (fn: () => Promise<unknown>, depois?: () => void) => {
    setErro(null)
    setProcessando(true)
    try {
      await fn()
      recarregar()
      props.aoMudar()
      setModo('ver')
      depois?.()
    } catch (x) {
      setErro(mensagemErro(x))
    } finally {
      setProcessando(false)
    }
  }

  // reordenar itens por arrastar (persiste a ordem)
  let arrastando: number | null = null
  const soltar = (destino: number) => {
    const de = arrastando
    arrastando = null
    const det = d.latest
    if (de === null || de === destino || !det) return
    const ids = det.itens.map((i) => i.id)
    const [movido] = ids.splice(de, 1)
    ids.splice(destino, 0, movido)
    void executar(() => api.importacoes.reordenarItens(props.id, ids))
  }

  const iniciarRecebimento = (det: ImportacaoDetalhe) => {
    const init: Record<number, string[]> = {}
    for (const it of det.itens) if (it.serializado === 1) init[it.id] = Array(it.quantidade).fill('')
    setSeries(init)
    setErro(null)
    setModo('receber')
  }
  const estornar = async () => {
    if (await confirmar('Estornar o recebimento?', 'As entradas em estoque serão revertidas (o histórico é preservado).', { rotulo: 'Estornar', perigo: true }))
      void executar(() => api.importacoes.estornar(props.id))
  }
  const excluir = async (det: ImportacaoDetalhe) => {
    const msg =
      det.status === 'RECEBIDO'
        ? 'Excluir DEFINITIVAMENTE esta importação recebida? As entradas em estoque serão removidas e o saldo ajustado. Esta ação é permanente e não pode ser desfeita.'
        : 'Excluir esta importação permanentemente? Esta ação não altera o estoque.'
    if (await confirmar('Excluir importação?', msg, { rotulo: 'Excluir', perigo: true })) void executar(() => api.importacoes.excluir(props.id), props.aoExcluida)
  }

  return (
    <Show when={d.latest} fallback={<p class="p-6 text-sm text-ink-subtle">{d.loading ? 'Carregando…' : 'Importação não encontrada.'}</p>}>
      {(det) => (
        <Painel
          class="max-h-[90dvh]"
          titulo={
            <span class="flex flex-wrap items-center gap-2">
              {det().numeroInvoice}
              <Etiqueta tom={TOM[det().status]}>{det().status}</Etiqueta>
            </span>
          }
          sub={
            <>
              {det().fornecedorNome}
              <Show when={det().dataPedido}> · Pedido: {det().dataPedido}</Show>
              <Show when={det().dataRecebimento}> · Recebido: {det().dataRecebimento!.slice(0, 10)}</Show>
              {' '}· Outras despesas: {brl(det().valorOutrasDespesas)}
            </>
          }
          aoFechar={props.aoFechar}
          rodape={
            <Show when={props.podeEditar && modo() === 'ver'}>
              <Show when={MANUAIS.includes(det().status) && det().status !== 'CANCELADO'}>
                <Selecao class="w-44" value={det().status} disabled={processando()} aria-label="Status" onChange={(e) => void executar(() => api.importacoes.setStatus(props.id, e.currentTarget.value as StatusImportacao))}>
                  <For each={MANUAIS}>{(s) => <option value={s}>{s}</option>}</For>
                </Selecao>
              </Show>
              <span class="ml-auto" />
              <Show when={det().status !== 'CANCELADO'}>
                <Button variant="ghost" size="sm" onClick={() => props.aoEditar(det())}>Editar</Button>
              </Show>
              <Show when={det().status === 'RECEBIDO'}>
                <Button variant="ghost" size="sm" onClick={() => void estornar()}>Estornar</Button>
              </Show>
              <Button variant="danger-ghost" size="sm" onClick={() => void excluir(det())}>Excluir</Button>
              <Show when={EDITAVEL.includes(det().status)}>
                <Button variant="accent" size="sm" onClick={() => iniciarRecebimento(det())}>Marcar como recebido</Button>
              </Show>
            </Show>
          }
        >
          <Erro mensagem={erro()} />
          <Show when={det().itens.length > 0} fallback={<Vazio titulo="Importação sem itens" />}>
            <div class="rounded-xl border border-edge overflow-auto">
              <table class={T.tabela}>
                <thead class="text-left text-xs text-ink-subtle border-b border-edge">
                  <tr>
                    <th class="px-3 py-2 font-medium">Modelo</th>
                    <th class="px-2 py-2 font-medium text-right">Qtd</th>
                    <th class="px-2 py-2 font-medium text-right" title="Alocado a clientes (vendido desta importação)">Vend.</th>
                    <th class="px-2 py-2 font-medium text-right">Est. real</th>
                    <th class="px-2 py-2 font-medium text-right">Custo un.</th>
                    <th class="px-2 py-2 font-medium text-right">Pond.</th>
                    <th class="px-2 py-2 font-medium text-right">Landed un.</th>
                    <th class="px-2 py-2" />
                  </tr>
                </thead>
                <tbody>
                  <For each={det().itens}>
                    {(it, idx) => {
                      const l = () => det().landed.find((x) => x.itemId === it.id)
                      return (
                        <>
                          <tr
                            class="border-t border-edge-muted"
                            draggable={props.podeEditar}
                            onDragStart={() => (arrastando = idx())}
                            onDragOver={(e) => arrastando !== null && e.preventDefault()}
                            onDrop={() => soltar(idx())}
                          >
                            <td class="px-3 py-1.5 text-ink">
                              <Show when={props.podeEditar}>
                                <span class="mr-1 cursor-grab select-none text-ink-placeholder" title="Arrastar para reordenar">⠿</span>
                              </Show>
                              {it.codigoModelo}
                              <Show when={it.serializado === 1}><span class="ml-1"><Etiqueta tom="aviso">série</Etiqueta></span></Show>
                            </td>
                            <td class="px-2 py-1.5 text-right tabular-nums text-ink">{it.quantidade}</td>
                            <td class="px-2 py-1.5 text-right tabular-nums text-ink-muted">{it.alocado || '—'}</td>
                            <td class="px-2 py-1.5 text-right tabular-nums font-medium text-ink">{it.estoqueReal}</td>
                            <td class="px-2 py-1.5 text-right tabular-nums text-ink-muted">{brl(it.custoUnitarioOrigem)}</td>
                            <td class="px-2 py-1.5 text-right tabular-nums text-ink-muted">{l() ? `${(l()!.ponderacao * 100).toFixed(2)}%` : '—'}</td>
                            <td class="px-2 py-1.5 text-right tabular-nums text-success">{brl(l()?.custoLandedUnitarioBRL)}</td>
                            <td class="px-2 py-1.5 text-right">
                              <Show when={props.podeEditar}>
                                <Button variant="ghost" size="sm" onClick={() => (setErro(null), setAlocItem(alocItem() === it.id ? null : it.id))}>alocar</Button>
                              </Show>
                            </td>
                          </tr>
                          <Show when={alocItem() === it.id}>
                            <tr class="bg-page/40">
                              <td colSpan={8} class="px-3 py-2">
                                <Alocar
                                  disponivel={it.estoqueReal}
                                  clientes={clientes() ?? []}
                                  aoCancelar={() => setAlocItem(null)}
                                  aoAlocar={(clienteId, quantidade, pedido) =>
                                    void executar(
                                      () => api.importacoes.alocar({ importacaoItemId: it.id, clienteId, quantidade, pedidoCliente: pedido, observacao: null }),
                                      () => setAlocItem(null),
                                    )
                                  }
                                />
                              </td>
                            </tr>
                          </Show>
                        </>
                      )
                    }}
                  </For>
                </tbody>
              </table>
            </div>
          </Show>

          <Show when={det().alocacoes.length > 0}>
            <div class="rounded-lg border border-edge p-3 text-sm">
              <div class="mb-1 text-xs font-medium uppercase tracking-wide text-ink-subtle">Alocações a clientes (vendidos desta importação)</div>
              <For each={det().alocacoes}>
                {(a, idx) => {
                  const codigo = det().itens.find((i) => i.id === a.importacaoItemId)?.codigoModelo ?? '—'
                  const anterior = idx() > 0 ? det().itens.find((i) => i.id === det().alocacoes[idx() - 1].importacaoItemId)?.codigoModelo ?? '—' : null
                  return (
                    <>
                      <Show when={codigo !== anterior}>
                        <div class="mt-2 px-1 text-xs font-semibold text-accent first:mt-0">{codigo}</div>
                      </Show>
                      <div class="flex items-center justify-between border-b border-edge-muted py-1 pl-4 pr-1 last:border-0">
                        <span class="text-ink-muted">
                          {a.quantidade}× <span class="text-ink">{a.clienteNome}</span>
                          <Show when={a.pedidoCliente}><span class="ml-2 text-xs text-ink-subtle">Pedido: {a.pedidoCliente}</span></Show>
                        </span>
                        <Show when={props.podeEditar}>
                          <button type="button" class="text-xs text-failure hover:underline" onClick={() => void executar(() => api.importacoes.removerAlocacao(a.id))}>remover</button>
                        </Show>
                      </div>
                    </>
                  )
                }}
              </For>
            </div>
          </Show>

          <div class="flex flex-wrap justify-end gap-x-6 gap-y-1 text-sm">
            <span class="text-ink-muted">Soma NF: <span class="text-ink tabular-nums">{brl(det().totais.somaValorNF)}</span></span>
            <span class="text-ink-muted">Outras despesas: <span class="text-ink tabular-nums">{brl(det().totais.outrasDespesasBRL)}</span></span>
            <span class="text-ink-muted">Valor final (landed): <span class="font-semibold text-ink tabular-nums">{brl(det().totais.totalLandedBRL)}</span></span>
          </div>

          <Show when={modo() === 'receber'}>
            <div class="flex flex-col gap-3 rounded-lg border border-success/40 bg-success/10 p-3">
              <div class="text-sm text-success">
                Confirmar recebimento — isto dá <b>entrada em estoque</b> (reversível só por estorno).
                <Show when={det().itens.some((i) => i.serializado === 1)}> Informe as séries das unidades (ou deixe em branco = pendente).</Show>
              </div>
              <For each={det().itens.filter((i) => i.serializado === 1)}>
                {(it) => (
                  <Campo rotulo={`Séries — ${it.codigoModelo} (${it.quantidade})`}>
                    <div class="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      <Index each={series()[it.id] ?? []}>
                        {(s, i) => (
                          <Input
                            placeholder={`Unidade ${i + 1}`}
                            value={s()}
                            onInput={(e) => setSeries({ ...series(), [it.id]: (series()[it.id] ?? []).map((v, j) => (j === i ? e.currentTarget.value : v)) })}
                          />
                        )}
                      </Index>
                    </div>
                  </Campo>
                )}
              </For>
              <div class="flex justify-end gap-2">
                <Button variant="ghost" size="sm" disabled={processando()} onClick={() => setModo('ver')}>Voltar</Button>
                <Button variant="accent" size="sm" disabled={processando()} onClick={() => void executar(() => api.importacoes.receber(props.id, series()))}>
                  {processando() ? 'Recebendo…' : 'Confirmar recebimento'}
                </Button>
              </div>
            </div>
          </Show>
        </Painel>
      )}
    </Show>
  )
}

function Alocar(props: { disponivel: number; clientes: Cliente[]; aoCancelar: () => void; aoAlocar: (clienteId: number, qtd: number, pedido: string | null) => void }) {
  const [busca, setBusca] = createSignal('')
  const [cliente, setCliente] = createSignal(0)
  const [pedido, setPedido] = createSignal('')
  const [qtd, setQtd] = createSignal('1')
  const achados = createMemo(() => {
    const q = busca().trim().toLowerCase()
    if (!q || cliente()) return []
    return props.clientes.filter((c) => c.razaoSocial.toLowerCase().includes(q) || (c.nomeFantasia ?? '').toLowerCase().includes(q) || (c.cnpjCpf ?? '').includes(busca().trim())).slice(0, 8)
  })
  return (
    <div class="flex flex-wrap items-center gap-2">
      <span class="text-xs text-ink-subtle">Alocar a um cliente (disponível: {props.disponivel}):</span>
      <div class="relative w-64">
        <Input value={busca()} placeholder="Buscar cliente…" onInput={(e) => (setBusca(e.currentTarget.value), setCliente(0))} />
        <Show when={achados().length > 0}>
          <div class="absolute z-20 mt-1 max-h-44 w-full overflow-auto rounded-md border border-edge bg-panel shadow-lg">
            <For each={achados()}>
              {(c) => (
                <button type="button" class="block w-full truncate px-3 py-1.5 text-left text-sm text-ink hover:bg-hover" onClick={() => (setCliente(c.id), setBusca(c.razaoSocial))}>
                  {c.razaoSocial}
                </button>
              )}
            </For>
          </div>
        </Show>
      </div>
      <Input class="w-40" value={pedido()} placeholder="Pedido do cliente" onInput={(e) => setPedido(e.currentTarget.value)} />
      <Input class="w-20" value={qtd()} placeholder="Qtd" onInput={(e) => setQtd(e.currentTarget.value)} />
      <Button variant="accent" size="sm" onClick={() => props.aoAlocar(cliente(), Math.floor(Number(qtd()) || 0), pedido().trim() || null)}>Alocar</Button>
      <Button variant="ghost" size="sm" onClick={() => props.aoCancelar()}>cancelar</Button>
    </div>
  )
}

type LinhaItem = { modeloId: number; codigoModelo: string; serializado: number; quantidade: string; custoUnitarioOrigem: string; lote: string }
const numero = (s: string) => {
  const n = Number(s.trim().replace(',', '.'))
  return Number.isFinite(n) ? n : NaN
}

function FormImportacao(props: { importacao: ImportacaoDetalhe | null; aoFechar: () => void; aoSalvar: () => void }) {
  const imp = props.importacao
  const [fornecedores] = createResource(() => api.importacoes.fornecedores())
  const [invoice, setInvoice] = createSignal(imp?.numeroInvoice ?? '')
  const [fornecedorId, setFornecedorId] = createSignal<number>(imp?.fornecedorId ?? 0)
  const [outras, setOutras] = createSignal((imp?.valorOutrasDespesas ?? 0).toString())
  const [dataPedido, setDataPedido] = createSignal(imp?.dataPedido ?? '')
  const [dataEmbarque, setDataEmbarque] = createSignal(imp?.dataEmbarque ?? '')
  const [dataChegada, setDataChegada] = createSignal(imp?.dataChegadaPrevista ?? '')
  const [obs, setObs] = createSignal(imp?.observacoes ?? '')
  const [itens, setItens] = createSignal<LinhaItem[]>(
    imp?.itens.map((it) => ({
      modeloId: it.modeloId, codigoModelo: it.codigoModelo, serializado: it.serializado,
      quantidade: it.quantidade.toString(), custoUnitarioOrigem: it.custoUnitarioOrigem.toString(), lote: it.lote ?? '',
    })) ?? [],
  )
  const [erro, setErro] = createSignal<string | null>(null)
  const [salvando, setSalvando] = createSignal(false)
  createEffect(() => {
    const f = fornecedores()
    if (!imp && f?.[0] && !fornecedorId()) setFornecedorId(f[0].id)
  })
  const mudar = (i: number, p: Partial<LinhaItem>) => setItens(itens().map((it, j) => (j === i ? { ...it, ...p } : it)))
  const adicionar = (m: ModeloLista) => {
    if (itens().some((it) => it.modeloId === m.id)) return
    setItens([...itens(), { modeloId: m.id, codigoModelo: m.codigoModelo, serializado: m.serializado, quantidade: '1', custoUnitarioOrigem: '0', lote: '' }])
  }
  const somaNF = () => itens().reduce((s, it) => s + (numero(it.quantidade) || 0) * (numero(it.custoUnitarioOrigem) || 0), 0)

  const salvar = async () => {
    setErro(null)
    // mesmo payload do app atual (moeda BRL; frete/seguro/impostos zerados; outras despesas rateadas por valor)
    const payload = {
      numeroInvoice: invoice(),
      fornecedorId: fornecedorId(),
      moeda: 'BRL',
      taxaCambio: null,
      valorFrete: 0,
      valorSeguro: 0,
      valorImpostos: 0,
      valorOutrasDespesas: numero(outras() || '0'),
      dataPedido: dataPedido() || null,
      dataEmbarque: dataEmbarque() || null,
      dataChegadaPrevista: dataChegada() || null,
      observacoes: obs() || null,
      itens: itens().map((it) => ({ modeloId: it.modeloId, quantidade: Math.floor(numero(it.quantidade)), custoUnitarioOrigem: numero(it.custoUnitarioOrigem), lote: it.lote || null })),
    }
    const r = importacaoSchema.safeParse(payload)
    if (!r.success) return setErro(r.error.issues[0]?.message ?? 'Dados inválidos.')
    setSalvando(true)
    try {
      if (imp) await api.importacoes.update(imp.id, r.data)
      else await api.importacoes.create(r.data)
      props.aoSalvar()
    } catch (x) {
      setErro(mensagemErro(x))
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Formulario
      aberto
      titulo={imp ? `Editar importação ${imp.numeroInvoice}` : 'Nova importação'}
      largura={820}
      aoFechar={props.aoFechar}
      aoEnviar={() => void salvar()}
      rodape={
        <>
          <Button type="button" variant="ghost" onClick={() => props.aoFechar()}>Cancelar</Button>
          <Button type="submit" variant="accent" disabled={salvando()}>{salvando() ? 'Salvando…' : 'Salvar'}</Button>
        </>
      }
    >
      <Erro mensagem={erro()} />
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Campo class="col-span-2" rotulo="Nº da invoice"><Input value={invoice()} onInput={(e) => setInvoice(e.currentTarget.value)} /></Campo>
        <Campo class="col-span-2" rotulo="Fornecedor">
          <Selecao value={fornecedorId()} onChange={(e) => setFornecedorId(Number(e.currentTarget.value))}>
            <option value={0}>—</option>
            <For each={fornecedores() ?? []}>{(f) => <option value={f.id} selected={f.id === fornecedorId()}>{f.nome}</option>}</For>
          </Selecao>
        </Campo>
        <Campo rotulo="Outras despesas (R$)" dica="Rateadas por valor entre os itens">
          <Input value={outras()} onInput={(e) => setOutras(e.currentTarget.value)} placeholder="0,00" />
        </Campo>
      </div>
      <div class="grid grid-cols-3 gap-3">
        <Campo rotulo="Data do pedido"><Input type="date" value={dataPedido() ?? ''} onInput={(e) => setDataPedido(e.currentTarget.value)} /></Campo>
        <Campo rotulo="Embarque"><Input type="date" value={dataEmbarque() ?? ''} onInput={(e) => setDataEmbarque(e.currentTarget.value)} /></Campo>
        <Campo rotulo="Chegada prevista"><Input type="date" value={dataChegada() ?? ''} onInput={(e) => setDataChegada(e.currentTarget.value)} /></Campo>
      </div>
      <div class="flex flex-col gap-1">
        <span class="text-xs text-ink-muted">Itens da importação</span>
        <div class="rounded-lg border border-edge">
          <Show when={itens().length > 0} fallback={<div class="px-3 py-3 text-sm text-ink-subtle">Nenhum item. Busque um modelo abaixo.</div>}>
            <div class="flex items-center gap-2 border-b border-edge px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-ink-subtle">
              <div class="flex-1">Modelo</div>
              <div class="w-20">Qtd</div>
              <div class="w-28">Custo un. (R$)</div>
              <div class="w-24">Lote</div>
              <div class="w-8" />
            </div>
          </Show>
          <Index each={itens()}>
            {(it, i) => (
              <div class="flex items-center gap-2 border-b border-edge-muted px-3 py-2 last:border-0">
                <div class="flex-1 text-sm text-ink">
                  {it().codigoModelo}
                  <Show when={it().serializado === 1}><span class="ml-1"><Etiqueta tom="aviso">série</Etiqueta></span></Show>
                </div>
                <Input class="w-20" value={it().quantidade} title="Quantidade" placeholder="Qtd" onInput={(e) => mudar(i, { quantidade: e.currentTarget.value })} />
                <Input class="w-28" value={it().custoUnitarioOrigem} title="Custo unitário R$ (NF de entrada)" placeholder="Custo un." onInput={(e) => mudar(i, { custoUnitarioOrigem: e.currentTarget.value })} />
                <Input class="w-24" value={it().lote} placeholder="Lote" onInput={(e) => mudar(i, { lote: e.currentTarget.value })} />
                <Button type="button" variant="ghost" size="sm" onClick={() => setItens(itens().filter((_, j) => j !== i))} aria-label="Remover item">✕</Button>
              </div>
            )}
          </Index>
          <div class="px-3 py-2">
            <BuscaModelo aoEscolher={adicionar} placeholder="Adicionar modelo (buscar código)…" excluirIds={itens().map((i) => i.modeloId)} />
          </div>
        </div>
        <div class="text-right text-xs text-ink-subtle">Soma NF: {num(somaNF())} R$</div>
      </div>
      <Campo rotulo="Observações"><AreaTexto value={obs() ?? ''} onInput={(e) => setObs(e.currentTarget.value)} /></Campo>
    </Formulario>
  )
}
